// The single definition of "is this value acceptable for this field".
//
// Every form in the app used to inline `new RegExp(fields[x].regex).test(v)`.
// That worked only because most patterns in fields.js carried a trailing
// "|null" so a raw null would stringify to "null" and match. Two problems with
// that: the alternative is unanchored, so it also waved through any value that
// merely *contained* "null" ("annulled9" passed a letters-only rule); and it
// silently coupled correctness to a piece of regex punctuation that looked
// like a typo. Emptiness is a real case with real rules — it belongs in code.
//
// The "|null" alternatives have been removed from fields.js. patternFor still
// strips a trailing one so an older or hand-written rule can't reintroduce the
// hole.

export function isEmptyValue(value) {
    return value === null || value === undefined || value === "";
}

/**
 * Is this field required *for this particular record*?
 *
 * Some requirements only hold for part of the fleet. A bond name, warehouse date
 * and daily demurrage describe a vehicle sitting in a customs bond — a car bought
 * in Kampala never sees one, so demanding them of a Local unit forced the user to
 * invent data. It also made Local vehicles unsavable through the picker wizard,
 * which skips the bond step entirely (totalSteps = Origin === "Imported" ? 7 : 6)
 * and so leaves BondName "" and DailyDemurrage 0 — and DailyDemurrage's regex
 * rejects 0.
 *
 * The condition is DECLARATIVE rather than a predicate function on purpose: the
 * server mirror (node/helpers/vehicleFields.js) has to carry the identical rule,
 * and a plain object can be compared for parity exactly, where two functions
 * cannot. `requiredWhen` sits alongside `required: true` rather than replacing it
 * so the many places reading `meta.required` directly — the red asterisk in
 * InputPair, the hidden-cost-field warning — keep their conservative answer.
 * Over-marking a field is cosmetic; under-enforcing one is the bug being fixed.
 *
 * @param {Object} meta   a fields.js entry
 * @param {Object} record the whole record, so a condition can read a sibling field
 */
export function isRequired(meta, record) {
    if (!meta) return false;
    const cond = meta.requiredWhen;
    if (!cond) return Boolean(meta.required);
    if (typeof cond !== "object" || !cond.field || !("equals" in cond)) {
        // Loud on purpose. A typo here would silently drop a requirement, which
        // is the exact failure this module exists to prevent; and because
        // fields.js is static it throws for everyone on the first check rather
        // than for one unlucky record.
        throw new Error(`requiredWhen must be { field, equals } — got ${JSON.stringify(cond)}`);
    }
    return String((record || {})[cond.field] ?? "") === String(cond.equals);
}

/**
 * The declared normalisations. Data in, data out — a map rather than a switch so
 * an unknown name is inert instead of throwing on every keystroke.
 */
const NORMALIZERS = {
    stripSpaces: (v) => String(v).replace(/\s+/g, ''),
    ugPhone: (v) => toUgNational(v),
    // Sellers only. A Ugandan dealer's SELLERS are routinely not Ugandan —
    // stock comes through Mombasa and Dar es Salaam, and the DB already holds a
    // Kenyan and a Tanzanian one. So a UG number canonicalises exactly as
    // ugPhone does, and a neighbour's stays as bare <cc><nsn>, which is how
    // those rows are already stored and what formatPhoneForDisplay (it only
    // rewrites a 256 prefix) already leaves alone.
    eacPhone: (v) => toUgNational(v),
};

/**
 * Canonicalise a phone number to Uganda's national form, 0XXXXXXXXX.
 *
 * Uganda's NSN is 9 digits, so the national form is always 10 characters and
 * always starts 0, while the form this app puts on its own wire (see
 * formatPhoneForStorage in UtilityFunctions.jsx) is always 12 digits and always
 * starts 256. A string cannot be both lengths, so folding one into the other is
 * unambiguous — but only when the length is checked. Stripping a leading "256"
 * on sight would mangle a foreign number that merely begins with those digits,
 * so every branch below pins the exact digit count it expects.
 *
 * Anything that matches nothing is handed back with its separators stripped
 * rather than mutilated further: the regex is about to reject it, and the
 * message should show the user a number they recognise as the one they typed.
 */
function toUgNational(v) {
    const d = String(v).replace(/[\s\-().+]/g, '');
    if (/^00256\d{9}$/.test(d)) return '0' + d.slice(5); //international access prefix
    if (/^256\d{9}$/.test(d)) return '0' + d.slice(3);   //our own storage form
    if (/^0\d{9}$/.test(d)) return d;                    //already canonical
    if (/^[1-9]\d{8}$/.test(d)) return '0' + d;          //bare NSN, trunk 0 dropped
    return d;
}

/**
 * Apply a field's declared normalisation, if it has one.
 *
 * Runs on BOTH sides. The browser calls it on blur so the user watches the value
 * become what will be stored — that visibility is the whole point, because a
 * silent rewrite teaches nothing and a refusal teaches only that the field is
 * awkward. The server calls it before validating, so a client that skipped it
 * (an old tab, a script, the import path) writes the same canonical value.
 *
 * Returns the value untouched when there is no rule, and leaves empty values
 * alone so a blank never becomes the string "".
 */
export function normalizeFieldValue(value, meta) {
    if (isEmptyValue(value)) return value;
    const fn = NORMALIZERS[meta?.normalize];
    return fn ? fn(value) : value;
}

export function patternFor(meta) {
    if (!meta?.regex) return null;
    return meta.regex.replace(/\|null$/, "");
}

/**
 * Check one value against one fields.js entry.
 *
 * Returns null when the value is acceptable, otherwise
 * `{ kind, message }` where kind is "required" | "maxLength" | "regex".
 * Callers decide how to word and place the message; `kind` is there so a caller
 * that wants distinct titles (the vehicle modal) doesn't have to string-match.
 *
 * Checks run in order and the first failure wins: an empty value is never
 * pattern-tested, and an over-long value reports its length rather than failing
 * with a character-set message.
 *
 * @param {*}      value  the cell value
 * @param {Object} meta   a fields.js entry
 * @param {Object} [record] the whole record — only consulted for meta.requiredWhen.
 *                 Omitting it on a conditionally-required field reads as "the
 *                 condition does not hold", i.e. not required.
 */
export function checkFieldValue(value, meta, record) {
    if (!meta) return null; //no rules recorded for this key — nothing to check
    const label = meta.displayName || "This field";

    if (isEmptyValue(value)) {
        return isRequired(meta, record) ? { kind: "required", message: `${label} is required` } : null;
    }

    const str = String(value);

    if (meta.maxLength && str.length > meta.maxLength) {
        return {
            kind: "maxLength",
            message: `${label} must be at most ${meta.maxLength} characters (this one is ${str.length}).`,
        };
    }

    const pattern = patternFor(meta);
    if (pattern && !(new RegExp(pattern)).test(str)) {
        return { kind: "regex", message: meta.message };
    }

    return null;
}

/** Convenience for the many call sites that only want a yes/no. */
export function fieldValueIsValid(value, meta, record) {
    return checkFieldValue(value, meta, record) === null;
}
